import { useUserSearchService } from '../services/useUserSearch.service';
import { useState } from 'react';
import { UserSearchResponse } from '../_types/user.types';
import { useUserSearchStorage } from './useUserSearchStorage';
import notification from '../../../components/ds/Notification';
import { useSchema } from './useSchema';
import { useClearAllData } from './useDataCleaner';
import { useParticipantId } from './useParticipantId';

interface UseSearchUserResult {
  searchUser: (username: string, captcha: string) => Promise<UserSearchResponse | null>;
  loading: boolean;
  error: string | null;
  userData: UserSearchResponse | null;
}

export const useSearchUser = (): UseSearchUserResult => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const {userData, setUserData} = useUserSearchStorage();
  const {checkIsOpenFlow} = useSchema();
  const {searchUserService} = useUserSearchService();
  const {clearRegistrationData} = useClearAllData();
  const {setParticipantId} = useParticipantId();

  const searchUser = async (username: string, captcha: string): Promise<UserSearchResponse | null> => {
    setLoading(true);
    setError(null);

    // Verificar se o fluxo é fechado antes de proceder
    const isOpenFlow = checkIsOpenFlow();

    // Limpar dados de cadastros anteriores antes de iniciar nova busca
    clearRegistrationData();

    try {
      console.log(`consolando username`, username, captcha)

      const response = await searchUserService({username, captcha});
      console.log(`consolando response`, response)

      // Verifica se o usuário foi encontrado
      if (response.userFound) {
        const userInfo: UserSearchResponse = {
          participantId: response.participantId,
          emailObfuscated: response.emailObfuscated,
          phoneObfuscated: response.phoneObfuscated,
          firstAccess: response?.firstAccess,
          onboardingFinished: response?.onboardingFinished,
          userFound: true
        };

        // Garantir que o participantId é uma string limpa antes de salvar
        if (response.participantId) {
          let cleanParticipantId = response.participantId;

          // Se o participantId veio como JSON string, fazer parse
          if (typeof cleanParticipantId === 'string' && cleanParticipantId.startsWith('"')) {
            try {
              cleanParticipantId = JSON.parse(cleanParticipantId);
            } catch {
              // Se não conseguir fazer parse, remover aspas manualmente
              cleanParticipantId = cleanParticipantId.replace(/^"|"$/g, '');
            }
          }

          console.log('Setting participantId:', {
            original: response.participantId,
            cleaned: cleanParticipantId
          });

          setParticipantId(cleanParticipantId);
        }

        setUserData(userInfo);

        return userInfo;
      }

      // Se userFound é false, verificar se o fluxo permite cadastro
      if (!isOpenFlow) {
        // Fluxo fechado - usuário não encontrado - exibir erro e não permitir continuar
        notification.error({
          message: 'Usuário não encontrado',
          description: 'Este usuário não está autorizado a acessar o sistema. Entre em contato com o administrador.',
          duration: 5
        });

        setError('Usuário não encontrado no fluxo fechado.');
        setUserData(null);

        return null;
      }

      // Se userFound é false e fluxo é aberto, retorna a resposta indicando que o usuário não foi encontrado
      const notFoundResponse: UserSearchResponse = {
        userFound: false,
        participantId: undefined,
        emailObfuscated: undefined,
        phoneObfuscated: undefined,
        firstAccess: undefined,
        onboardingFinished: undefined
      };

      // Armazena o estado de "usuário não encontrado" para referência futura
      setUserData(notFoundResponse);

      return notFoundResponse;

    } catch (err: any) {
      console.error('Erro no useSearchUser:', err);
      setUserData(null);

      // Para qualquer erro que não seja relacionado ao userFound, mostra erro genérico
      notification.error(
        {
          message: 'Erro inesperado',
          description: 'Erro ao processar a conta, tente novamente mais tarde!',
          duration: 0
        }
      );
      setError(err.message || 'Erro ao processar a conta.');

      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    searchUser,
    loading,
    error,
    userData
  };
};

 